import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSocket } from '../hooks/useSocket';
import QueueCard, { WIDE_COUNTER_MIN, servingCount, type CardDensity } from './QueueCard';

/**
 * Fallbacks only — the real values are per-location and set in the dashboard
 * (Manage Queues -> Board layout). Used before the first load resolves, or if
 * the API omits them.
 */
const DEFAULT_QUEUES_PER_PAGE = 6;
const DEFAULT_ROTATE_SECONDS = 15;

/** How often the board re-syncs server-derived figures. */
const BOARD_REFRESH_MS = 10000;

const PublicDisplay = () => {
  const { isConnected, queues, joinQueue, fetchQueue, setInitialQueues } = useSocket();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [locationName, setLocationName] = useState('');
  const [page, setPage] = useState(0);
  const [queuesPerPage, setQueuesPerPage] = useState(DEFAULT_QUEUES_PER_PAGE);
  const [rotateSeconds, setRotateSeconds] = useState(DEFAULT_ROTATE_SECONDS);
  const initializedRef = useRef(false);
  const queueIdsRef = useRef<string[]>([]);

  // The configured page size applies at full width. Narrower screens lay the
  // cards out 1 or 2 per row, so cap the page there — otherwise a page sized
  // for a TV would run off the bottom of a smaller screen.
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const perPage = isLg ? queuesPerPage : Math.min(queuesPerPage, isMd ? 4 : 2);

  // A queue with many counters open spans the full width, so it occupies a
  // whole row rather than one slot. Paginating on card COUNT would let such a
  // page run past the bottom of the screen — the exact problem the page size
  // exists to prevent — so pages are filled by the space each card takes.
  const columns = isLg ? 3 : isMd ? 2 : 1;
  const pages = useMemo(() => {
    const out: typeof queues[] = [];
    let current: typeof queues = [];
    let used = 0;
    for (const q of queues) {
      const cost = Math.min(perPage, servingCount(q) >= WIDE_COUNTER_MIN ? columns : 1);
      if (used + cost > perPage && current.length) {
        out.push(current);
        current = [];
        used = 0;
      }
      current.push(q);
      used += cost;
    }
    if (current.length) out.push(current);
    return out.length ? out : [[]];
  }, [queues, perPage, columns]);

  const totalPages = pages.length;
  // Guard against a stale page index when queues are added/removed or the
  // viewport changes the page size.
  const safePage = page >= totalPages ? 0 : page;
  const visibleQueues = pages[safePage] || [];
  const shownBefore = pages.slice(0, safePage).reduce((n, p) => n + p.length, 0);

  // The board fills the screen with whatever THIS page holds — a lone queue
  // gets the whole screen, not a third of a fixed 3-column grid. Column count
  // comes from the page's card count (capped by viewport width); rows stretch
  // via gridAutoRows so there is no dead space below the cards.
  const visCount = visibleQueues.length;
  const layoutCols = Math.min(
    columns,
    visCount <= 1 ? 1 : visCount === 2 ? 2 : visCount === 3 ? 3 : visCount === 4 ? 2 : 3
  );
  const density: CardDensity = visCount <= 1 ? 'xl' : visCount <= 4 ? 'lg' : 'md';

  // Rotate through the pages so that no queue is permanently invisible. A
  // lobby screen is never scrolled, so anything past the first page would
  // otherwise never be seen by anyone.
  useEffect(() => {
    if (totalPages <= 1) {
      if (page !== 0) setPage(0);
      return;
    }
    const timer = setInterval(() => {
      setPage(prev => (prev + 1) % totalPages);
    }, rotateSeconds * 1000);
    return () => clearInterval(timer);
  }, [totalPages, page, rotateSeconds]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  /**
   * Fetch the authoritative board snapshot.
   *
   * Sockets alone are not enough: a `customer_called` event patches NOW SERVING
   * on the client, but the waiting count, wait estimate and the just-called
   * list are all DERIVED server-side. Those stayed frozen at page-load values,
   * so after a few calls a lobby screen was showing a stale queue length and a
   * wait estimate to match — on a board that is never reloaded.
   */
  const loadQueues = useCallback(async (isInitial: boolean) => {
      try {
        if (isInitial) {
          setLoading(true);
          setError('');
        }

        // Get location ID from URL path (e.g., /display/location-id or /location-id)
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const locationId = pathParts[pathParts.length - 1] || import.meta.env.VITE_DEFAULT_LOCATION_ID || 'demo-location';

        if (isInitial) console.log('Initializing public display with locationId:', locationId);

        // Fetch display data from secured API endpoint
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token') || '';
        const response = await fetch(`${apiUrl}/api/public-display/${locationId}?token=${encodeURIComponent(token)}`);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to load display data');
        }

        const data = await response.json();
        if (isInitial) console.log('Display data loaded:', data);

        // The endpoint already returns the location; showing its name tells a
        // viewer which branch/lobby board they are looking at.
        if (data.location?.name) {
          setLocationName(String(data.location.name));
        }
        // Board layout is configured per location in the dashboard; the API
        // clamps the values, so they're used as given.
        if (data.location?.display) {
          const d = data.location.display;
          if (Number(d.queuesPerPage) > 0) setQueuesPerPage(Number(d.queuesPerPage));
          if (Number(d.rotateSeconds) > 0) setRotateSeconds(Number(d.rotateSeconds));
        }

        // Replace with the server snapshot. This is authoritative, so it also
        // corrects anything the socket patched optimistically.
        if (data.queues) {
          setInitialQueues(data.queues);
          const ids = data.queues.map((q: { id: string }) => q.id);
          // Only (re)join rooms for queues we aren't already subscribed to.
          const newIds = ids.filter((id: string) => !queueIdsRef.current.includes(id));
          queueIdsRef.current = ids;
          newIds.forEach((id: string) => joinQueue(id));
        }

        initializedRef.current = true;

      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load queue information';
        // A failed background poll must not blank a working board — keep showing
        // the last good data and let the next poll recover.
        if (isInitial) setError(errorMessage);
        console.error('Error loading queues:', err);
      } finally {
        if (isInitial) setLoading(false);
      }
  }, [joinQueue, setInitialQueues]);

  useEffect(() => {
    if (initializedRef.current) return;
    loadQueues(true);
  }, [loadQueues]);

  // Re-sync the derived figures. The socket keeps NOW SERVING instant; this
  // keeps the counts honest.
  useEffect(() => {
    const timer = setInterval(() => loadQueues(false), BOARD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadQueues]);

  // Handle connection changes separately — rejoin queue rooms on reconnect
  useEffect(() => {
    if (!initializedRef.current || !isConnected) return;
    queueIdsRef.current.forEach(id => joinQueue(id));
  }, [isConnected, joinQueue]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress size={80} sx={{ mb: 3 }} />
        <Typography variant="h4" color="white">
          Loading Queue Information...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // Exactly one screen tall: a lobby TV is never scrolled, so the grid
        // stretches to the bottom edge instead of leaving dead space.
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
        color: 'white',
        py: 3,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Container
        maxWidth={false}
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        {/*
          Single-row header. This was a centred stack (84px logo, 5rem wordmark,
          a "Queue Management System" strapline and the clock on their own
          lines) which ate enough vertical space to push the last queue card off
          the bottom of a TV. The strapline is now the LOCATION name — a viewer
          standing in the lobby already knows it is a queue board; what they
          cannot tell is which branch's board it is.
        */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              component="img"
              src="/favicon.svg"
              alt=""
              sx={{
                width: { xs: 40, md: 52 },
                height: { xs: 40, md: 52 },
                borderRadius: '12px',
                boxShadow: '0 12px 30px -8px rgba(99,102,241,0.5)',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 300,
                lineHeight: 1,
                fontSize: { xs: '2rem', md: '2.75rem' },
                whiteSpace: 'nowrap',
              }}
            >
              QPlease
              <Box component="span" sx={{ fontSize: '0.38em', verticalAlign: 'super', ml: '0.12em' }}>
                ™
              </Box>
            </Typography>
          </Box>

          {locationName && (
            <Typography
              sx={{
                flex: '1 1 auto',
                textAlign: 'center',
                opacity: 0.85,
                fontWeight: 400,
                fontSize: { xs: '1.25rem', md: '1.75rem' },
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {locationName}
            </Typography>
          )}

          <Typography
            sx={{
              opacity: 0.6,
              fontSize: { xs: '1rem', md: '1.25rem' },
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {formatTime(currentTime)}
          </Typography>
        </Box>

        {/* Connection Status */}
        {!isConnected && (
          <Alert
            severity="warning"
            sx={{
              mb: 4,
              fontSize: '1.2rem',
              '& .MuiAlert-icon': { fontSize: '2rem' }
            }}
          >
            Connection lost - displaying cached data
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 4,
              fontSize: '1.2rem',
              '& .MuiAlert-icon': { fontSize: '2rem' }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Queues Grid */}
        {queues.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography
              variant="h3"
              sx={{
                opacity: 0.7,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              No Active Queues
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mt: 2,
                opacity: 0.5,
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}
            >
              Please check back later
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${layoutCols}, 1fr)`,
              gridAutoRows: '1fr',
              gap: 3,
            }}
          >
            {visibleQueues.map(queue => (
              <QueueCard key={queue.id} queue={queue} density={density} />
            ))}
          </Box>
        )}

        {/* Page indicator — only when there is more than one page, so a viewer
            knows more queues are coming rather than assuming this is all. */}
        {totalPages > 1 && (
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {Array.from({ length: totalPages }, (_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: i === safePage ? 28 : 10,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: i === safePage ? 'primary.main' : 'rgba(255,255,255,0.25)',
                    transition: 'width 0.3s ease, background-color 0.3s ease',
                  }}
                />
              ))}
            </Box>
            <Typography sx={{ opacity: 0.6, fontSize: { xs: '0.85rem', md: '1rem' } }}>
              Showing {shownBefore + 1}–{shownBefore + visibleQueues.length} of {queues.length} queues
            </Typography>
          </Box>
        )}

        {/* Footer. The "scan the QR code" instruction used to appear whether or
            not a QR was on screen — each card now carries its own, so this only
            points at them when at least one is actually rendered. */}
        <Box sx={{ textAlign: 'center', mt: 3, opacity: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
            {queues.some(q => q.joinQr)
              ? `Scan a queue's QR code to join • Updated ${formatTime(currentTime)}`
              : `Updated ${formatTime(currentTime)}`}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicDisplay;